(async () => {
    const {
        loadScript,
        loadStyle
    } = await import(window.theme.modules.utils);

    const baseComponent = await import(window.theme.modules.baseComponent);

    // Matches the theme's mobile breakpoint (see core.css .hide-mobile).
    const NATIVE_MOBILE_QUERY = '(max-width: 991px)';

    customElements.define('video-player', class extends baseComponent.default {
        elements = {
            player: '[data-player]',
            HTMLPlayer: 'video'
        }

        // Plyr replaces the provider's chrome with its own overlay, cropping the iframe
        // to hide YouTube's branding. On mobile that overlay ended up sitting on top of
        // YouTube's own controls and toggling itself on every swipe, so players that opt
        // in are handed straight to the provider there instead.
        get usesNativeEmbed() {
            return this.hasAttribute('native-mobile')
                && !this.hasAttribute('internal')
                && window.matchMedia(NATIVE_MOBILE_QUERY).matches;
        }

        get iframe() {
            return this.$player?.querySelector('iframe');
        }

        render() {
            if (!this.usesNativeEmbed) return;
            this.setAttribute('native', '');
            // Plyr's wrapper class is what crops and repositions the iframe. With no Plyr
            // instance driving it, the iframe has to sit in the slide unmodified.
            this.$player?.classList.remove('plyr__video-embed');
            this._baseSrc = this._embedSrc();
            if (!this._baseSrc) return;
            if (this._baseSrc !== this.iframe.src) this.iframe.src = this._baseSrc;
            // While inert the iframe ignores pointer events, so a tap on the video lands
            // on this element instead. Once armed the iframe takes them and this stops
            // firing, which is what makes the provider's own controls usable.
            // Swiper suppresses the click after a drag, so swipes never arm it.
            this.$player.addEventListener('click', () => this._arm());
        }

        _embedSrc(autoplay = false) {
            if (!this.iframe?.src) return null;
            const src = new URL(this.iframe.src, window.location.href);
            // Both providers show their own controls unless told not to.
            src.searchParams.delete('controls');
            // Without this iOS takes the video fullscreen instead of playing in place.
            src.searchParams.set('playsinline', '1');
            if (autoplay) {
                src.searchParams.set('autoplay', '1');
            } else {
                src.searchParams.delete('autoplay');
            }
            return src.toString();
        }

        // [armed] hands the surface to the provider: CSS restores pointer events on the
        // iframe and slide-show.js exempts it from Swiper's gesture handling, so the
        // native controls work. Reloading with autoplay is what starts playback --
        // posting a play command instead relies on the frame's API already listening,
        // which is not guaranteed and left the video unplayable when it was not.
        _arm() {
            if (this.hasAttribute('armed')) return;
            this.setAttribute('armed', '');
            this.iframe.src = this._embedSrc(true);
        }

        _disarm() {
            if (!this.hasAttribute('armed')) return;
            // No player API to call, so resetting the src is what stops playback -- and
            // it restores the poster and the inert swipe surface along with it.
            this.removeAttribute('armed');
            this.iframe.src = this._baseSrc;
        }

        async onIntersect(isIntersecting) {
            if (this.usesNativeEmbed) {
                if (!isIntersecting) this._disarm();
                return;
            }
            if(isIntersecting && !this.player) {
                await this._initPlayer();
            }
            if(!isIntersecting && this.player && this.player.playing) {
                this.player.pause();
            }
        }

        async _initPlayer() {
            loadStyle('https://cdn.plyr.io/3.7.8/plyr.css');
            if(!window.Plyr) {
                await loadScript(window.theme.scripts.Plyr);
            }
            this.player = new window.Plyr(this.hasAttribute('internal') ? this.$HTMLPlayer : this.$player, {
                fullscreen: {iosNative: true}
            });
        }
    })
})();
